import { useCallback, useMemo, type ReactNode } from 'react';
import { Toaster, toast } from 'sonner';

import { SnackbarContext, type ShowSnackbarInput } from './snackbar_context';

const DEFAULT_DURATION = 5000;

/*
 * SnackbarProvider — public surface preserved (`useSnackbar().showSnackbar`),
 * implementation now sits on `sonner`. Sonner owns queueing, motion, and
 * accessibility; we only forward the message and map severity to the matching
 * toast variant.
 *
 * Position matches the prior MUI Snackbar (bottom-right). Styling is themed
 * via DS tokens through Tailwind classes on the toast container.
 */
export function SnackbarProvider({
  children,
}: Readonly<{ children: ReactNode }>): React.JSX.Element {
  const showSnackbar = useCallback((input: ShowSnackbarInput): void => {
    const duration = input.autoHideDuration ?? DEFAULT_DURATION;
    const message = input.message;
    switch (input.severity) {
      case 'success':
        toast.success(message, { duration });
        return;
      case 'error':
        toast.error(message, { duration });
        return;
      case 'warning':
        toast.warning(message, { duration });
        return;
      case 'info':
      case undefined:
      default:
        toast.info(message, { duration });
    }
  }, []);

  const value = useMemo(() => ({ showSnackbar }), [showSnackbar]);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      <Toaster
        position="bottom-right"
        richColors
        closeButton
        toastOptions={{
          classNames: {
            toast:
              'rounded-lg border border-border bg-popover text-foreground font-sans text-base shadow-popover',
            title: 'font-medium',
            description: 'text-muted-foreground',
          },
        }}
      />
    </SnackbarContext.Provider>
  );
}
